import { AdmDataGrid } from "@components/common/data-grid/adm-data-grid";
import { useWorkspaceMembersTableColumns } from "@pages/workspace-profile/tab-contents/workspace-members-tab/use-workspace-members-table-columns";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchCompanyMembers } from "@store/workspace/workspace-thunks";
import { BaseCompanyProps } from "@custom-types/base-types";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { Spinner } from "@components/common/spinner/spinner";
import { ContentLayout } from "@pages/content-layout";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useAppNavigation } from "@router/use-app-navigation";
import { InviteMemberToWorkspace } from "@pages/workspace-profile/invite-member-to-workspace";
import { CSVDataUtils } from "@utils/csv-data";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

const ERROR_MESSAGE = "Could not load members of the workspace";

/** Fetch workspace members and display them in a table */
export function WorkspaceMembers({
  company,
}: BaseCompanyProps): React.JSX.Element {
  const { handleErrorWithToast } = useErrorHandler();
  const [shouldShowErrorMessage, setShouldShowErrorMessage] =
    useState<boolean>(false);
  const { navigateToProfilePage } = useAppNavigation();

  const columns = useWorkspaceMembersTableColumns({ company });

  const { members, fetchingCompany } = useAppSelector(companyStateSelector);

  const dispatch = useAppDispatch();

  // Fetches the company members
  useEffect(() => {
    dispatch(fetchCompanyMembers({ companyId: company.id }))
      .unwrap()
      .catch((error) => {
        handleErrorWithToast({
          id: `CompanyMembers-${Date.now().toString()}`,
          title: ERROR_MESSAGE,
          error,
        });
        setShouldShowErrorMessage(true);
      });
  }, [company.id, dispatch, handleErrorWithToast]);

  if (fetchingCompany.companyMembers === "pending") {
    return <Spinner />;
  }

  return (
    <ContentLayout contentSx={{ overflowY: "hidden" }}>
      <AdmDataGrid
        entities={members}
        uniqueIdKey="identity"
        tableType="workspace-members-table"
        columns={columns}
        errorMessage={ERROR_MESSAGE}
        shouldShowErrorMessage={shouldShowErrorMessage}
        isRowClickable={FaroCoreApiUtils.Member.isMemberActive}
        onRowClick={(memberId) => {
          const member = members.find((member) => member.identity === memberId);
          if (member && FaroCoreApiUtils.Member.isMemberActive(member)) {
            navigateToProfilePage({
              subjectType: "USER",
              subjectId: memberId,
            });
          }
        }}
        additionalToolbarButtons={<InviteMemberToWorkspace company={company} />}
        onExport={() => CSVDataUtils.downloadMemberData(members)}
      />
    </ContentLayout>
  );
}
